import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Image, Modal, Spinner } from 'react-bootstrap';
import {
  FaBan,
  FaChevronDown,
  FaChevronUp,
  FaComments,
  FaDiscord,
  FaFacebook,
  FaGift,
  FaEdit,
  FaInstagram,
  FaLinkedin,
  FaPinterest,
  FaReddit,
  FaSnapchatGhost,
  FaStar,
  FaTimes,
  FaTiktok,
  FaTwitch,
  FaTwitter,
  FaUserShield,
  FaYoutube,
} from 'react-icons/fa';
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { firestore } from '../views/firebaseConfig';
import PostCard from './PostCard';
import MessageDetailModal from './MessageDetailModal';
import SettingsModal from '../views/SettingsModal';
import { PLAYER_SOCIAL_PLATFORMS } from '../utils/playerProfileFields';
import './PlayerProfileModal.css';

const socialIcons = {
  instagramUrl: FaInstagram,
  tiktokUrl: FaTiktok,
  youtubeUrl: FaYoutube,
  facebookUrl: FaFacebook,
  xUrl: FaTwitter,
  threadsUrl: FaComments,
  snapchatUrl: FaSnapchatGhost,
  linkedinUrl: FaLinkedin,
  twitchUrl: FaTwitch,
  discordUrl: FaDiscord,
  redditUrl: FaReddit,
  pinterestUrl: FaPinterest,
};

function getPrice(item) {
  const value = Number(item.price);
  if (!Number.isFinite(value)) return 'N/A';
  return `$${value.toFixed(2)}`;
}

function ProfileMetric({ label, value }) {
  if (!value) return null;
  return (
    <div className="ppm2-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default function PlayerProfileModal({ show, onHide, player }) {
  const { user } = useAuth();
  const currentUserId = user?.uid;

  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [starCount, setStarCount] = useState(0);
  const [userStarCount, setUserStarCount] = useState(0);
  const [saleItems, setSaleItems] = useState([]);
  const [loadingSale, setLoadingSale] = useState(false);
  const [pockits, setPockits] = useState([]);
  const [loadingPockits, setLoadingPockits] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pockits');
  const [showChat, setShowChat] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    if (!show || !player?.id) return;
    const id = player.id;
    setLoadingProfile(true);
    setProfileData(null);

    getDoc(doc(firestore, 'userprofiles', id))
      .then((snap) => setProfileData(snap.exists() ? { id: snap.id, ...snap.data() } : player))
      .catch(() => setProfileData(player))
      .finally(() => setLoadingProfile(false));

    fetchStarStats(id);
    fetchSaleItems(id);
    fetchPockits(id);
  }, [show, player]);

  useEffect(() => setImgLoading(true), [profileData?.profileImageUrl]);

  async function fetchStarStats(id) {
    try {
      const snap = await getDoc(doc(firestore, 'playerStars', id));
      const arr = snap.exists() ? snap.data().starCount || [] : [];
      const total = arr.reduce((sum, entry) => sum + (Number(entry.stars) || 0), 0);
      setStarCount(total);
      const mine = arr.find((entry) => entry.interestedId === currentUserId);
      setUserStarCount(mine?.stars || 0);
    } catch {
      setStarCount(0);
      setUserStarCount(0);
    }
  }

  async function giveStar(id) {
    if (!currentUserId || id === currentUserId) return;

    const refStars = doc(firestore, 'playerStars', id);
    const now = Timestamp.now().toMillis();
    const snap = await getDoc(refStars);
    const arr = snap.exists() ? [...(snap.data().starCount || [])] : [];
    const idx = arr.findIndex((entry) => entry.interestedId === currentUserId);

    if (idx > -1) {
      arr[idx] = { ...arr[idx], stars: (Number(arr[idx].stars) || 0) + 1, timestamp: now };
    } else {
      arr.push({ interestedId: currentUserId, stars: 1, timestamp: now });
    }

    await setDoc(refStars, { receiverId: id, starCount: arr }, { merge: true });
    const allTimeTotal = arr.reduce((sum, entry) => sum + (Number(entry.stars) || 0), 0);
    await fetchStarStats(id);
    await sendStarNotification(currentUserId, id, allTimeTotal, idx > -1 ? arr[idx].stars : 1);
  }

  async function sendStarNotification(senderId, receiverId, allTimeTotal, justThisMany) {
    const [senderSnap, receiverSnap] = await Promise.all([
      getDoc(doc(firestore, 'userprofiles', senderId)),
      getDoc(doc(firestore, 'userprofiles', receiverId)),
    ]);
    const senderName = senderSnap.exists() ? senderSnap.data().nickname : 'Someone';
    const receiverName = receiverSnap.exists() ? receiverSnap.data().nickname : 'you';
    const inbox = collection(firestore, 'globalinbox');
    const snap = await getDocs(query(
      inbox,
      where('forsaleId', '==', 'stars'),
      where('interestedId', 'in', [senderId, receiverId]),
      where('receiverId', 'in', [senderId, receiverId])
    ));

    let convoId;
    if (!snap.empty) {
      convoId = snap.docs[0].id;
    } else {
      const base = {
        conversationId: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        forsaleId: 'stars',
        interestedId: senderId,
        receiverId,
        conversation: [],
        blockedConversation: false,
        deletedConversation: { [senderId]: 'active', [receiverId]: 'active' },
        lastModified: serverTimestamp(),
      };
      convoId = (await addDoc(inbox, base)).id;
    }

    const starWord = justThisMany === 1 ? 'a star' : `${justThisMany} stars`;
    const text = `⭐ ${senderName} sent you ${starWord}. You now have ${allTimeTotal} total.`;
    await updateDoc(doc(firestore, 'globalinbox', convoId), {
      conversation: arrayUnion({
        senderID: senderId,
        receiverID: receiverId,
        text: receiverName ? text : text,
        timestamp: Timestamp.fromDate(new Date()),
        readStatus: 'unread',
      }),
      lastModified: serverTimestamp(),
      [`deletedConversation.${senderId}`]: 'active',
      [`deletedConversation.${receiverId}`]: 'active',
    });
  }

  async function fetchSaleItems(id) {
    setLoadingSale(true);
    try {
      const snap = await getDocs(query(
        collection(firestore, 'forsale'),
        where('status', '==', 'active'),
        where('receiverId', '==', id)
      ));
      setSaleItems(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch {
      setSaleItems([]);
    } finally {
      setLoadingSale(false);
    }
  }

  async function fetchPockits(id) {
    setLoadingPockits(true);
    try {
      const snap = await getDocs(query(
        collection(firestore, 'feedPost'),
        where('userId', '==', id),
        where('status', '==', 'active')
      ));
      setPockits(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch {
      setPockits([]);
    } finally {
      setLoadingPockits(false);
    }
  }

  async function blockUser() {
    if (!currentUserId || !player.id || !window.confirm('Block this player?')) return;

    const batch = writeBatch(firestore);
    const meRef = doc(firestore, 'blockedUsers', currentUserId);
    const themRef = doc(firestore, 'blockedBy', player.id);
    const myList = (await getDoc(meRef)).data()?.blockedList || [];
    if (!myList.includes(player.id)) myList.push(player.id);
    batch.set(meRef, { blockedList: myList }, { merge: true });

    const theirList = (await getDoc(themRef)).data()?.blockedByUsers || [];
    if (!theirList.includes(currentUserId)) theirList.push(currentUserId);
    batch.set(themRef, { blockedByUsers: theirList }, { merge: true });

    const convos = await getDocs(query(
      collection(firestore, 'globalinbox'),
      where('interestedId', 'in', [currentUserId, player.id]),
      where('receiverId', 'in', [currentUserId, player.id])
    ));
    convos.forEach((snap) => batch.update(snap.ref, {
      blockedConversation: true,
      blockComms: currentUserId,
      lastModified: Timestamp.now(),
    }));

    await batch.commit();
    onHide();
  }

  const openImage = (url) => {
    if (!url) return;
    setSelectedImage(url);
    setShowImageModal(true);
  };
  const openPost = (post) => { setSelectedPost(post); setShowPostModal(true); };
  const closePost = () => { setShowPostModal(false); setSelectedPost(null); };

  if (!player) return null;
  const data = profileData || player;
  const isSelf = data.id === currentUserId;
  const displayName = data.nickname || data.name || 'Player';
  const profileImageUrl = data.profileImageUrl || 'https://placehold.co/320x320/101010/FFD45C?text=P4R';
  const keyStrengths = Array.isArray(data.keyStrengths) ? data.keyStrengths.slice(0, 2) : [];
  const socials = PLAYER_SOCIAL_PLATFORMS.filter((platform) => data[platform.field]);
  const showReadMore = (data.bio || '').length > 130;

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        centered
        scrollable
        dialogClassName="ppm2-modal modal-dialog-scrollable"
        contentClassName="ppm2-content"
      >
        <Modal.Body className="ppm2-body p-0 d-flex flex-column">
          <div className="ppm2-header-inline d-flex align-items-center px-3 py-2">
            <FaStar className="ppm2-star-icon me-2" />
            <span className="ppm2-star-count">{starCount}</span>
            <h5 className="ppm2-title flex-grow-1 m-0">{displayName}</h5>
            <Button variant="link" size="sm" onClick={onHide} className="ppm2-close-btn"><FaTimes /></Button>
          </div>

          <div className="ppm2-inner-content flex-grow-1">
            {loadingProfile ? (
              <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
              <>
                <section className={`ppm2-profile ${collapsed ? 'is-collapsed' : ''}`}>
                  <button className="ppm2-collapse-btn" onClick={() => setCollapsed(!collapsed)} type="button">
                    {collapsed ? <FaChevronDown /> : <FaChevronUp />}
                  </button>

                  {!collapsed && (
                    <>
                      <div className="ppm2-img-wrapper mx-auto">
                        {imgLoading && <div className="ppm2-img-spinner"><Spinner size="sm" /></div>}
                        <Image
                          src={profileImageUrl}
                          className="ppm2-img"
                          onLoad={() => setImgLoading(false)}
                          onError={() => setImgLoading(false)}
                          onClick={() => openImage(profileImageUrl)}
                        />
                      </div>

                      <div className="ppm2-name-stack">
                        <h2>{displayName}</h2>
                        <div className="ppm2-badge-row">
                          {data.onlineStatus === 'online' && <Badge>Visible</Badge>}
                          {data.coachStatus === 'on' && <Badge><FaUserShield /> Coach</Badge>}
                          {starCount > 0 && <Badge><FaStar /> {starCount}</Badge>}
                        </div>
                      </div>

                      <div className="ppm2-metrics-grid">
                        <ProfileMetric label="R4ting" value={data.r4ting || data.fargoRating} />
                        <ProfileMetric label="Local" value={data.otherRating} />
                        <ProfileMetric label="Pockits" value={pockits.length} />
                        <ProfileMetric label="Listings" value={saleItems.length} />
                      </div>

                      {keyStrengths.length > 0 && (
                        <div className="ppm2-strength-row">
                          {keyStrengths.map((strength) => <span key={strength}>{strength}</span>)}
                        </div>
                      )}

                      <p className={`ppm2-bio ${bioExpanded ? '' : 'collapsed'}`}>{data.bio || 'No bio yet.'}</p>
                      {showReadMore && (
                        <Button variant="link" className="ppm2-readmore-btn" onClick={() => setBioExpanded(!bioExpanded)}>
                          {bioExpanded ? 'Show less' : 'Read more'}
                        </Button>
                      )}

                      {socials.length > 0 && (
                        <div className="ppm2-socials">
                          {socials.map((platform) => {
                            const Icon = socialIcons[platform.field] || FaComments;
                            return (
                              <a key={platform.field} href={data[platform.field]} target="_blank" rel="noreferrer" aria-label={platform.label}>
                                <Icon />
                              </a>
                            );
                          })}
                        </div>
                      )}

                      {data.cueImageUrl && (
                        <button type="button" className="ppm2-cue-strip" onClick={() => openImage(data.cueImageUrl)}>
                          <img src={data.cueImageUrl} alt="Cue" />
                          <span>Cue media</span>
                        </button>
                      )}

                      <div className="ppm2-action-grid">
                        {isSelf ? (
                          <Button className="ppm2-action-btn primary" onClick={() => setShowEditor(true)}>
                            <FaEdit /> Edit profile
                          </Button>
                        ) : (
                          <Button className="ppm2-action-btn primary" onClick={() => giveStar(data.id)} disabled={!currentUserId}>
                            <FaGift /> Star {userStarCount ? `(${userStarCount})` : ''}
                          </Button>
                        )}
                        {!isSelf && (
                          <Button className="ppm2-action-btn" onClick={() => { onHide(); setShowChat(true); }} disabled={!currentUserId}>
                            <FaComments /> Chat
                          </Button>
                        )}
                        {!isSelf && currentUserId && (
                          <Button className="ppm2-action-btn danger" onClick={blockUser}>
                            <FaBan /> Block
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </section>

                <div className="ppm2-tabs px-3">
                  <button type="button" className={activeTab === 'pockits' ? 'active' : ''} onClick={() => setActiveTab('pockits')}>Pockits</button>
                  <button type="button" className={activeTab === 'sale' ? 'active' : ''} onClick={() => setActiveTab('sale')}>Shop</button>
                </div>

                <div className="ppm2-content-area px-3 pb-4">
                  {activeTab === 'sale' ? (
                    loadingSale ? <div className="text-center py-3"><Spinner /></div> : (
                      <div className="ppm2-grid">
                        {saleItems.length === 0 && <div className="ppm2-empty">No listings yet.</div>}
                        {saleItems.map((item) => (
                          <Card key={item.id} className="ppm2-card h-100">
                            <Card.Img variant="top" src={item.imageUrl || item.imageUrls?.[0] || '/placeholder.png'} className="ppm2-thumb-img" />
                            <Card.Body>
                              <Card.Title>{item.title || 'Listing'}</Card.Title>
                              <Card.Text>{getPrice(item)}</Card.Text>
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    )
                  ) : loadingPockits ? (
                    <div className="text-center py-3"><Spinner /></div>
                  ) : (
                    <div className="ppm2-grid">
                      {pockits.length === 0 && <div className="ppm2-empty">No pockits yet.</div>}
                      {pockits.map((post) => (
                        <Card key={post.id} className="ppm2-card ppm2-thumbnail h-100" onClick={() => openPost(post)}>
                          {(post.image1 || post.imageUrl) && <Card.Img variant="top" src={post.image1 || post.imageUrl} className="ppm2-thumb-img" />}
                          <Card.Body>
                            <Card.Title>{post.postTitle || post.title || 'Pockit'}</Card.Title>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} size="lg" centered dialogClassName="ppm2-img-modal">
        <Modal.Body className="text-center"><Image src={selectedImage || profileImageUrl} fluid /></Modal.Body>
      </Modal>

      {!isSelf && currentUserId && (
        <MessageDetailModal
          show={showChat}
          onClose={() => setShowChat(false)}
          forsaleId="nearbyChat"
          interestedId={currentUserId}
          receiverId={data.id}
          receiverNickname={displayName}
          receiverAvatarUrl={profileImageUrl}
        />
      )}

      {isSelf && <SettingsModal show={showEditor} onClose={() => setShowEditor(false)} />}

      <Modal show={showPostModal} onHide={closePost} size="lg" centered dialogClassName="ppm2-post-modal">
        <Modal.Body className="position-relative">{selectedPost && <PostCard post={selectedPost} isFullscreen onClose={closePost} disableProfileOpen />}</Modal.Body>
      </Modal>
    </>
  );
}
